import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, User, Users, ChevronRight, Check, Home, Building2, Archive, ChevronDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { useCompany } from '@/hooks/useCompany';
import type { Tables } from '@/integrations/supabase/types';

type Patient = Tables<'patients'>;

interface PatientWithRole extends Patient {
  role: string;
  memberCount?: number;
  isCompanyPatient: boolean;
  companyName?: string | null;
  companyLogoUrl?: string | null;
}

export default function PatientsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { focusedPatient, setFocusedPatientId } = useFocusedPatient();
  const { isCompanyMember, isCompanyManagerOrCoordinator, company } = useCompany();

  const {
    data: patients = [],
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['patients-list', user?.id, company?.companyId, isCompanyManagerOrCoordinator],
    queryFn: async (): Promise<PatientWithRole[]> => {
      if (!user) return [];

      const { data: links, error: linksError } = await supabase
        .from('patient_user_links')
        .select('patient_id, role')
        .eq('user_id', user.id)
        .eq('active', true);

      if (linksError) throw linksError;

      // For company managers/coordinators, also include all patients assigned to the company
      let companyAssignmentPatientIds: string[] = [];
      if (isCompanyManagerOrCoordinator && company?.companyId) {
        const { data: assignments, error: assignmentsError } = await supabase
          .from('company_patient_assignments')
          .select('patient_id')
          .eq('company_id', company.companyId)
          .eq('is_active', true);
        if (assignmentsError) throw assignmentsError;
        companyAssignmentPatientIds = (assignments || []).map(a => a.patient_id);
      }

      const linkPatientIds = (links || []).map(l => l.patient_id);
      const allPatientIds = Array.from(new Set([...linkPatientIds, ...companyAssignmentPatientIds]));

      if (allPatientIds.length === 0) return [];

      // Patients, company assignments and team members in parallel (no per-patient loop).
      const [patientsResult, companyResult, membersResult] = await Promise.all([
        supabase.from('patients').select('*').in('id', allPatientIds),
        supabase
          .from('company_patient_assignments')
          .select('patient_id, care_companies(name, logo_url)')
          .in('patient_id', allPatientIds)
          .eq('is_active', true),
        supabase
          .from('patient_user_links')
          .select('patient_id')
          .in('patient_id', allPatientIds)
          .eq('active', true),
      ]);

      if (patientsResult.error) throw patientsResult.error;
      if (companyResult.error) throw companyResult.error;
      if (membersResult.error) throw membersResult.error;

      const companyByPatient = new Map<string, { name: string; logoUrl: string | null }>();
      (companyResult.data || []).forEach((a: any) => {
        const name = a.care_companies?.name ?? '';
        const logoUrl = a.care_companies?.logo_url ?? null;
        if (name) companyByPatient.set(a.patient_id, { name, logoUrl });
      });

      const memberCountByPatient = new Map<string, number>();
      (membersResult.data || []).forEach((m: any) => {
        memberCountByPatient.set(m.patient_id, (memberCountByPatient.get(m.patient_id) ?? 0) + 1);
      });

      const patientsWithRoles: PatientWithRole[] = (patientsResult.data || []).map((patient) => {
        const link = (links || []).find(l => l.patient_id === patient.id);

        // For company managers/coordinators without direct link, treat as 'owner' (company owner)
        const role = link?.role || (isCompanyManagerOrCoordinator ? 'owner' : 'observer');

        const companyInfo = companyByPatient.get(patient.id);
        return {
          ...patient,
          role,
          memberCount: memberCountByPatient.get(patient.id) ?? 0,
          isCompanyPatient: !!companyInfo,
          companyName: companyInfo?.name ?? null,
          companyLogoUrl: companyInfo?.logoUrl ?? null,
        };
      });

      patientsWithRoles.sort((a, b) => a.nickname.localeCompare(b.nickname));

      return patientsWithRoles;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: true,
    // Sempre revalida ao abrir a tela: o cache serve de fallback imediato,
    // mas mudanças feitas fora dela (novo paciente, arquivamento) aparecem na hora.
    refetchOnMount: 'always',
  });


  const [showArchived, setShowArchived] = useState(false);

  const { personalPatients, companyPatients, suspendedPatients, archivedPatients } = useMemo(() => {
    const personal: PatientWithRole[] = [];
    const company: PatientWithRole[] = [];
    const suspended: PatientWithRole[] = [];
    const archived: PatientWithRole[] = [];
    for (const p of patients) {
      const status = (p as any).status;
      if (status === 'archived') {
        archived.push(p);
      } else if (status === 'suspended') {
        suspended.push(p);
      } else if (p.isCompanyPatient) {
        company.push(p);
      } else {
        personal.push(p);
      }
    }
    return { personalPatients: personal, companyPatients: company, suspendedPatients: suspended, archivedPatients: archived };
  }, [patients]);

  const handlePatientClick = (patient: PatientWithRole) => {
    setFocusedPatientId(patient.id);
    navigate(`/app/patients/${patient.id}`);
  };

  const getRoleLabel = (role: string, isCompanyPatient: boolean) => {
    switch (role) {
      case 'owner': return isCompanyPatient ? t('roles.manager') : t('roles.owner');
      case 'caregiver': return t('roles.caregiver');
      case 'observer': return t('roles.observer');
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-primary/10 text-primary';
      case 'caregiver': return 'bg-success/10 text-success';
      case 'observer': return 'bg-secondary/10 text-secondary';
      default: return 'bg-secondary/10 text-secondary';
    }
  };

  const renderPatientCard = (patient: PatientWithRole) => {
    const isFocused = focusedPatient?.id === patient.id;
    const isArchived = (patient as any).status === 'archived';
    const isSuspended = (patient as any).status === 'suspended';

    return (
      <Card
        key={patient.id}
        className={`cursor-pointer hover:bg-accent/50 transition-colors ${isFocused ? 'ring-2 ring-primary bg-primary/5' : ''} ${isArchived || isSuspended ? 'opacity-60' : ''}`}
        onClick={() => handlePatientClick(patient)}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <SignedAvatar
                src={patient.photo_url}
                bucket="patient-photos"
                className="w-14 h-14"
                fallback={
                  <span className="bg-primary/10 text-primary text-lg flex items-center justify-center w-full h-full">
                    {patient.nickname.charAt(0).toUpperCase()}
                  </span>
                }
              />
              {isFocused && (
                <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                  <Check className="w-3 h-3" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold truncate">{patient.nickname}</h3>
                {isFocused && (
                  <span className="text-xs text-primary font-medium">{t('patient.inFocus')}</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(patient.role)}`}>
                  {getRoleLabel(patient.role, patient.isCompanyPatient)}
                </span>
                {isArchived && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground inline-flex items-center gap-1">
                    <Archive className="w-3 h-3" />
                    {t('patient.archive.badge', { defaultValue: 'Arquivado' })}
                  </span>
                )}
                {isSuspended && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-warning/15 text-warning inline-flex items-center gap-1">
                    <Archive className="w-3 h-3" />
                    {t('patient.suspended.badge', { defaultValue: 'Suspenso' })}
                  </span>
                )}
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {patient.memberCount}
                </span>
              </div>
              {patient.companyName && (
                <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                  <SignedAvatar
                    src={patient.companyLogoUrl ?? null}
                    bucket="company-logos"
                    className="w-4 h-4"
                    fallback={
                      <span className="bg-primary/10 text-primary w-full h-full flex items-center justify-center rounded-full">
                        <Building2 className="w-2.5 h-2.5" />
                      </span>
                    }
                  />
                  <span className="truncate">{patient.companyName}</span>
                </div>
              )}
            </div>

            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <AppLayout
      title={t('patient.myPatients')}
      showBack
      onBack={() => navigate('/app')}
      showFAB={false}
    >
      <div className="p-4">
        <Button
          className="w-full mb-4"
          onClick={() => navigate('/app/patients/new')}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('patient.newPatient')}
        </Button>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="w-14 h-14 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State — never confused with "no patients" */}
        {!isLoading && isError && (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
              <h3 className="font-semibold mb-2">
                {t('patient.loadError', { defaultValue: 'Não foi possível carregar seus pacientes' })}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('patient.loadErrorDescription', { defaultValue: 'Verifique sua conexão e tente novamente.' })}
              </p>
              <Button onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                {t('common.retry', { defaultValue: 'Tentar novamente' })}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !isError && patients.length === 0 && (

          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t('patient.noPatients')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('patient.noPatientsDescription')}
              </p>
              <Button onClick={() => navigate('/app/patients/new')}>
                <Plus className="w-4 h-4 mr-2" />
                {t('common.registerPatient')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Grouped Patients List */}
        {!isLoading && patients.length > 0 && (
          <div className="space-y-4">
            {/* Personal patients */}
            {personalPatients.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Home className="w-4 h-4 text-primary" />
                  <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    {t('patient.personalPatients')}
                  </h2>
                </div>
                <div className="space-y-3">
                  {personalPatients.map(renderPatientCard)}
                </div>
              </div>
            )}

            {/* Company patients */}
            {companyPatients.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    {t('patient.companyPatients')}
                  </h2>
                </div>
                <div className="space-y-3">
                  {companyPatients.map(renderPatientCard)}
                </div>
              </div>
            )}

            {/* Suspended (subscription downgrade) */}
            {suspendedPatients.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Archive className="w-4 h-4 text-warning" />
                  <h2 className="text-sm font-semibold text-warning uppercase tracking-wide">
                    {t('patient.suspended.section', { defaultValue: 'Suspensos por limite do plano' })} ({suspendedPatients.length})
                  </h2>
                </div>
                <div className="space-y-3">
                  {suspendedPatients.map(renderPatientCard)}
                </div>
              </div>
            )}

            {archivedPatients.length > 0 && (
              <div>
                <button
                  type="button"
                  className="flex items-center gap-2 mb-3 w-full text-left"
                  onClick={() => setShowArchived(v => !v)}
                >
                  <Archive className="w-4 h-4 text-muted-foreground" />
                  <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex-1">
                    {t('patient.archive.section', { defaultValue: 'Arquivados' })} ({archivedPatients.length})
                  </h2>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showArchived ? 'rotate-180' : ''}`} />
                </button>
                {showArchived && (
                  <div className="space-y-3">
                    {archivedPatients.map(renderPatientCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
